import { getDecisionNodeAtPath, getSimplrAccountIdOfSelectedTicket } from '../../../../extractFromState';
import { addNoteToSelectedTicket, setLoading } from '../../../index';
import { clearReturns, selectReturn, setLoadingReturnId, setReturns } from './ReturnActions';
import { showError } from 'util/errorPopupUtil';
import { getLoadingReturnId, getSelectedOrderNumber, getSelectedReturn } from '../../../../selectors/tools';
import isEmpty from 'lodash/isEmpty';
import makeRequest from 'api/makeRequest';
import environment from 'environment';
import { CANCEL_RETURN_ACTION_TYPE, PROCESS_RETURN_ACTION_TYPE } from 'constants/returnActions';

/**
 * Get high level returns info
 * @param {object} options
 * @param {string} options.orderNumber
 * @param {number} [options.limit]
 */
export const searchReturns = (options) => async (dispatch, getState) => {
  const { orderNumber, limit = 100 } = options;

  const state = getState();

  const params = {
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderNumber,
    limit,
  };

  try {
    dispatch(clearReturns());
    dispatch(setLoading(true, 'searchPage'));
    const data = await makeRequest(`${environment.eCommerce.RETURNS_BASE_URL}/returns`, {
      params,
    });

    dispatch(setReturns(data.returns));
  } catch (error) {
    showError(error);
  } finally {
    dispatch(setLoading(false, 'searchPage'));
  }
};

const clearSelectedReturnData = () => (dispatch) => {
  dispatch(selectReturn({}));
};

/**
 * Handle return select logic
 * @param {string} simplrAccountId
 * @param {object} orderReturn
 * @param {string} orderReturn.returnNumber
 * @param {string} orderReturn.returnId
 * @return {Function}
 */
export const selectReturnFromList = (simplrAccountId, orderReturn) => (dispatch, getState) => {
  const requestedReturnChanged = getSelectedReturn(getState()).returnId !== orderReturn.returnId;

  // empty object passed if user clicks on already selected return
  const returnIsBeingDeselected = isEmpty(orderReturn);

  if (requestedReturnChanged || returnIsBeingDeselected) {
    dispatch(clearSelectedReturnData());

    if (returnIsBeingDeselected) {
      // prevent previously triggered/still active API calls from loading
      return dispatch(setLoadingReturnId(null));
    }

    return dispatch(fetchAndSelectReturn(simplrAccountId, orderReturn));
  }
};

/**
 * Get order details from tools
 * @param {string} simplrAccountId
 * @param {object} orderReturn
 * @param {string} orderReturn.returnId
 * @return {Function}
 */
export const fetchAndSelectReturn = (simplrAccountId, orderReturn) => async (dispatch, getState) => {
  const { returnId } = orderReturn;

  try {
    dispatch(setLoadingReturnId(returnId));

    const params = {
      simplrAccountId,
      returnId,
    };

    // pickBy used for cutting empty params
    const result = await makeRequest(`${environment.eCommerce.RETURNS_BASE_URL}/return`, {
      params,
    });

    // user may have selected another return during query
    const returnStillSelected = getLoadingReturnId(getState()) === returnId;

    if (!returnStillSelected) {
      return;
    }

    if (result.return) {
      dispatch(selectReturn(result.return));
    } else {
      dispatch(selectReturn(orderReturn));
    }
  } catch (error) {
    showError(error);
    dispatch(setLoadingReturnId(null));
  }
};

export const returnNoteDetails = (actionType, selectedReturn) => `Action: ${actionType}
orderId: ${selectedReturn.orderId}
orderName: ${selectedReturn.orderName}
orderNumber: ${selectedReturn.orderNumber}
returnTotal: ${selectedReturn.returnTotal}
carrier: ${selectedReturn.carrier}
labelStatus: ${selectedReturn.labelStatus}
trackingNumber: ${selectedReturn.trackingNumber}
destinationId: ${selectedReturn.destinationId}`;

export const createReturnNotesIfEnabled = (actionType) => async (dispatch, getState) => {
  const state = getState();
  const { enableReturnNote } = getDecisionNodeAtPath(state);
  if (!enableReturnNote) {
    return;
  }
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const selectedReturn = getSelectedReturn(state);

  const notes = returnNoteDetails(actionType, selectedReturn);

  const payload = {
    simplrAccountId,
    returnId: String(selectedReturn.returnId),
    notes,
  };

  await dispatch(addNoteToSelectedTicket(notes));
  await makeRequest(`${environment.eCommerce.RETURNS_BASE_URL}/createReturnNotes`, {
    method: 'post',
    data: payload,
  });
};

export const addAllowlistItem = () => async (dispatch, getState) => {
  const state = getState();
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  // later pass the type to function, currently this is the only type so hard-coding it
  const type = 'order';
  const orderNumber = getSelectedOrderNumber(state);

  const payload = {
    simplrAccountId,
    type,
    value: orderNumber,
  };

  await makeRequest(`${environment.eCommerce.RETURNS_BASE_URL}/addAllowlistItem`, {
    method: 'post',
    data: payload,
  });
};

export const processReturn = () => async (dispatch, getState) => {
  const state = getState();
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const selectedReturn = getSelectedReturn(state);

  const payload = {
    simplrAccountId,
    conversationId: state?.selectedTicketId,
    returnId: String(selectedReturn.returnId),
    orderName: selectedReturn.orderName,
  };

  await makeRequest(`${environment.eCommerce.RETURNS_BASE_URL}/processReturn`, {
    method: 'post',
    data: payload,
  });

  await dispatch(createReturnNotesIfEnabled(PROCESS_RETURN_ACTION_TYPE));
};

export const cancelReturn = () => async (dispatch, getState) => {
  const state = getState();
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const selectedReturn = getSelectedReturn(state);

  const payload = {
    simplrAccountId,
    returnId: String(selectedReturn.returnId),
  };

  await makeRequest(`${environment.eCommerce.RETURNS_BASE_URL}/cancelReturn`, {
    method: 'post',
    data: payload,
  });

  await dispatch(createReturnNotesIfEnabled(CANCEL_RETURN_ACTION_TYPE));
};
